// ===========================================
// P&L ANALYSIS — ratios, channel economics, insights
// ===========================================
// Pure computation over the same payloads the CPG P&L build-down reads —
// no new endpoints, no estimates, no generated text. Every sentence here is
// a template filled with real numbers and rendered ONLY when its inputs
// exist; when something is missing the UI says what to enter and where.
// Reference bands live in lib/pnlBenchmarks.ts and never mix with these
// figures — they are rules of thumb, clearly captioned as such.

import { fmtMoneyShort } from '../utils/cpg';
import { formatByCurrency } from './money';

// ---------- input shapes (structural mirrors of the API responses) ----------
export interface AnalysisMonthlyCell { month: string; revenueCents: number | null; marginCents: number | null }
export interface AnalysisCatRow {
  category: string; label: string; customers: number; orders: number;
  revenueCents: number | null; cogsCents: number | null; deliveryCents: number | null;
  marginCents: number | null; marginPct: number | null; monthly: AnalysisMonthlyCell[];
}
export interface AnalysisCatSummary { months: string[]; rows: AnalysisCatRow[]; hasCosts: boolean }
export interface AnalysisDeduction { customerId?: string; amountCents?: number; deductionDate?: string | null; currency?: string }
export interface AnalysisDedSummary {
  totals?: { deductionCents: number | null; revenueCents: number | null; rateBps: number | null };
  customers?: Array<{ customerId: string; customerName: string; currency?: string; revenueCents: number; deductionCents: number }>;
}
export interface AnalysisInvoice {
  status: string; totalCents?: number; amountPaidCents?: number; currencyCode?: string;
  issuedAt?: string; sentAt?: string | null; createdAt?: string; paidAt?: string | null;
  customer?: { paymentTerms?: string | null } | null;
}
export interface AnalysisCustomer { id: string; category?: string | null }
export interface AnalysisDeliverySummary {
  totals?: { drops: number; deliveryCents: number | null };
  accounts?: Array<{ customerId: string; drops: number; deliveryCents: number }>;
}

export interface ChannelEcon {
  category: string; label: string; accounts: number; orders: number;
  revenueCents: number; sharePct: number;
  gmPct: number | null;
  deliveryCents: number; deliveryPct: number | null; drops: number | null;
  dedCents: number | null; dedPct: number | null;
}

export interface PnlAnalysis {
  grossCents: number;
  // Ratio strip
  gmPct: number | null;                 // gross margin, deductions included — null until costs exist
  dedRatePct: number | null;            // from the reconciliation summary's rateBps
  deliveryCents: number; deliveryPct: number | null; drops: number;
  avgDaysToPay: number | null; paidInvoiceCount: number; statedTermsDays: number | null;
  openArLabel: string | null;           // outstanding A/R, per-currency honest
  over60Label: string | null; over60Count: number; oldestOpenDays: number | null;
  activeAccounts: number | null; revPerAccountCents: number | null;
  // Channel economics + commentary
  channels: ChannelEcon[];
  commentary: string[];
  dedMixedCurrency: boolean;            // deductions span currencies → omit per-channel %
  // Insights
  insights: string[];
  lastMonthLabel: string | null;
  lastIsPartial: boolean;
}

const shortMonth = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, (m || 1) - 1, 1).toLocaleDateString('en-US', { month: 'short' });
};
const pct1 = (n: number) => (Math.round(n * 10) / 10).toFixed(1).replace(/\.0$/, '');

export function buildPnlAnalysis(input: {
  cats: AnalysisCatSummary | null | undefined;
  dedSummary: AnalysisDedSummary | null | undefined;
  deductions: AnalysisDeduction[];
  deliverySummary: AnalysisDeliverySummary | null | undefined;
  invoices: AnalysisInvoice[];
  customers: AnalysisCustomer[];
  categoryLabels: Record<string, string>;
}): PnlAnalysis | null {
  const { cats, dedSummary, deductions, deliverySummary, invoices, customers, categoryLabels } = input;
  if (!cats || cats.months.length === 0) return null;

  const months = cats.months;
  const rows = cats.rows.filter((r) => (r.revenueCents || 0) > 0 || r.orders > 0);
  if (rows.length === 0) return null;
  const labelOf = (r: AnalysisCatRow) => categoryLabels[r.category] || r.label;

  // Totals — same convention as the build-down (null revenue cells, which only
  // happen when an FX rate is missing, fold to 0 rather than inventing a rate).
  const grossCents = rows.reduce((s, r) => s + (r.revenueCents || 0), 0);
  const deliveryCents = rows.reduce((s, r) => s + (r.deliveryCents || 0), 0);
  const cogsCents = cats.hasCosts ? rows.reduce((s, r) => s + (r.cogsCents || 0), 0) : null;
  const dedTotal = dedSummary?.totals?.deductionCents || 0;
  const netCents = grossCents - dedTotal;
  const gmPct = cats.hasCosts && cogsCents != null && netCents > 0
    ? Math.round(((netCents - cogsCents - deliveryCents) / netCents) * 100) : null;
  const dedRatePct = dedSummary?.totals?.rateBps != null ? dedSummary.totals.rateBps / 100 : null;
  const drops = deliverySummary?.totals?.drops ?? 0;
  const deliveryPct = deliveryCents > 0 && grossCents > 0 ? (deliveryCents / grossCents) * 100 : null;

  // ---------- invoices: days-to-pay + aging ----------
  const anchor = (i: AnalysisInvoice) => new Date(i.sentAt || i.issuedAt || i.createdAt || 0).getTime();
  let paySum = 0; let paidInvoiceCount = 0;
  const termsSeen = new Map<number, number>();
  const openArBy: Record<string, number> = {}; const over60By: Record<string, number> = {};
  let over60Count = 0; let oldestOpenDays: number | null = null;
  for (const inv of invoices) {
    // "Net 30" (and "2/10 Net 30") → 30; a bare number counts too.
    const terms = inv.customer?.paymentTerms || '';
    const n = /net\s*(\d+)/i.exec(terms)?.[1] || /(\d+)\s*$/.exec(terms.trim())?.[1];
    if (n) termsSeen.set(Number(n), (termsSeen.get(Number(n)) || 0) + 1);
    if (inv.status === 'paid' && inv.paidAt) {
      const t0 = anchor(inv);
      const days = (new Date(inv.paidAt).getTime() - t0) / 86400000;
      if (t0 > 0 && Number.isFinite(days) && days >= 0) { paySum += days; paidInvoiceCount += 1; }
      continue;
    }
    if (inv.status !== 'unpaid' && inv.status !== 'partial') continue;
    const out = (inv.totalCents || 0) - (inv.amountPaidCents || 0);
    if (out <= 0) continue;
    const t0 = anchor(inv);
    const days = t0 > 0 ? Math.floor((Date.now() - t0) / 86400000) : 0;
    const cur = String(inv.currencyCode || 'USD').toUpperCase();
    openArBy[cur] = (openArBy[cur] || 0) + out;
    if (days > 60) { over60By[cur] = (over60By[cur] || 0) + out; over60Count += 1; }
    if (oldestOpenDays == null || days > oldestOpenDays) oldestOpenDays = days;
  }
  const avgDaysToPay = paidInvoiceCount > 0 ? Math.round(paySum / paidInvoiceCount) : null;
  const statedTermsDays = termsSeen.size > 0
    ? [...termsSeen.entries()].sort((a, b) => b[1] - a[1])[0][0] : null;
  const openArLabel = Object.keys(openArBy).length > 0 ? formatByCurrency(openArBy) : null;
  const over60Label = over60Count > 0 ? formatByCurrency(over60By) : null;

  // ---------- revenue per active account + concentration ----------
  // Both read the reconciliation summary so numerator and denominator share a
  // basis (all non-cancelled orders, per customer).
  const dedCustomers = (dedSummary?.customers || []).filter((c) => c.revenueCents > 0);
  const dedRevTotal = dedSummary?.totals?.revenueCents ?? null;
  const activeAccounts = dedSummary ? dedCustomers.length : null;
  const revPerAccountCents = dedRevTotal != null && dedCustomers.length > 0
    ? Math.round(dedRevTotal / dedCustomers.length) : null;
  const allUsdAccounts = dedCustomers.every((c) => (c.currency || 'USD') === 'USD');
  const topAccount = allUsdAccounts && dedRevTotal
    ? [...dedCustomers].sort((a, b) => b.revenueCents - a.revenueCents)[0] || null : null;
  const topAccountShare = topAccount && dedRevTotal ? Math.round((topAccount.revenueCents / dedRevTotal) * 100) : null;

  // ---------- channel economics ----------
  const catByCustomer = new Map(customers.map((c) => [c.id, c.category || 'uncategorized']));
  const dedMixedCurrency = deductions.some((d) => (d.currency || 'USD') !== 'USD');
  const dedByCat = new Map<string, number>();
  if (!dedMixedCurrency) {
    for (const d of deductions) {
      const cat = catByCustomer.get(d.customerId || '') || 'uncategorized';
      dedByCat.set(cat, (dedByCat.get(cat) || 0) + (d.amountCents || 0));
    }
  }
  const dropsByCat = new Map<string, number>();
  for (const a of deliverySummary?.accounts || []) {
    const cat = catByCustomer.get(a.customerId) || 'uncategorized';
    dropsByCat.set(cat, (dropsByCat.get(cat) || 0) + (a.drops || 0));
  }
  const channels: ChannelEcon[] = rows.map((r) => {
    const rev = r.revenueCents || 0;
    const ded = dedMixedCurrency ? null : (dedByCat.get(r.category) || 0);
    return {
      category: r.category, label: labelOf(r), accounts: r.customers, orders: r.orders,
      revenueCents: rev, sharePct: grossCents > 0 ? Math.round((rev / grossCents) * 100) : 0,
      gmPct: r.marginPct,
      deliveryCents: r.deliveryCents || 0,
      deliveryPct: (r.deliveryCents || 0) > 0 && rev > 0 ? ((r.deliveryCents || 0) / rev) * 100 : null,
      drops: dropsByCat.has(r.category) ? dropsByCat.get(r.category)! : null,
      dedCents: ded,
      dedPct: ded != null && ded > 0 && rev > 0 ? (ded / rev) * 100 : ded === 0 ? 0 : null,
    };
  }).sort((a, b) => b.revenueCents - a.revenueCents);

  // Channel commentary — template sentences, rendered only when derivable.
  const commentary: string[] = [];
  const costed = channels.filter((c) => c.gmPct != null && c.revenueCents >= 10_000);
  if (costed.length >= 2) {
    const best = costed.reduce((a, b) => (b.gmPct! > a.gmPct! ? b : a));
    const worst = costed.reduce((a, b) => (b.gmPct! < a.gmPct! ? b : a));
    const gap = best.gmPct! - worst.gmPct!;
    if (gap >= 3) {
      let line = `${worst.label} margin trails ${best.label} by ${gap} pts`;
      const dGap = (worst.deliveryPct || 0) - (best.deliveryPct || 0);
      if (dGap >= gap / 2 && worst.deliveryCents > 0) {
        line += ` — delivery cost is most of the gap: ${fmtMoneyShort(worst.deliveryCents)}${worst.drops ? ` across ${worst.drops} drops` : ''}`;
      }
      commentary.push(`${line}.`);
    }
  }
  if (!dedMixedCurrency && dedTotal > 0) {
    const topDed = [...channels].sort((a, b) => (b.dedCents || 0) - (a.dedCents || 0))[0];
    if (topDed && (topDed.dedCents || 0) / dedTotal >= 0.75 && topDed.dedPct != null) {
      commentary.push(`Deductions land almost entirely on ${topDed.label}: ${fmtMoneyShort(topDed.dedCents!)}, ${pct1(topDed.dedPct)}% of its revenue.`);
    }
  }

  // ---------- insights: month-over-month, from the monthly cells ----------
  const insights: string[] = [];
  const lastIdx = months.length - 1;
  const prevIdx = months.length - 2;
  const nowYm = new Date().toISOString().slice(0, 7);
  const lastIsPartial = months[lastIdx] === nowYm;
  const lastL = shortMonth(months[lastIdx]);
  const prevL = prevIdx >= 0 ? shortMonth(months[prevIdx]) : null;
  const partialNote = lastIsPartial ? ` (${lastL} to date)` : '';

  // Dated deductions per month — same fold the build-down uses.
  const monthIndex = new Map(months.map((m, i) => [m, i]));
  const dedByMonth = months.map(() => 0);
  for (const d of deductions) {
    const m = d.deductionDate ? new Date(d.deductionDate).toISOString().slice(0, 7) : null;
    const i = m != null ? monthIndex.get(m) : undefined;
    if (i !== undefined) dedByMonth[i] += d.amountCents || 0;
  }

  if (prevIdx >= 0) {
    // 1 · Biggest mover — channel margin when costs exist, revenue otherwise.
    const metric = (r: AnalysisCatRow, i: number) =>
      cats.hasCosts ? (r.monthly[i]?.marginCents ?? 0) : (r.monthly[i]?.revenueCents || 0);
    let mover: { label: string; delta: number } | null = null;
    for (const r of rows) {
      const delta = metric(r, lastIdx) - metric(r, prevIdx);
      if (Math.abs(delta) >= 5_000 && (!mover || Math.abs(delta) > Math.abs(mover.delta))) mover = { label: labelOf(r), delta };
    }
    if (mover) {
      const word = cats.hasCosts ? 'channel margin' : 'revenue';
      const dir = mover.delta > 0 ? `+${fmtMoneyShort(mover.delta)}` : `−${fmtMoneyShort(Math.abs(mover.delta))}`;
      insights.push(`Biggest ${word} mover: ${mover.label}, ${dir} vs ${prevL}${partialNote}.`);
    }

    // 2 · Gross-margin direction — monthly margin minus dated deductions, over net.
    if (cats.hasCosts) {
      const gmAt = (i: number) => {
        const rev = rows.reduce((s, r) => s + (r.monthly[i]?.revenueCents || 0), 0);
        const mar = rows.reduce((s, r) => s + (r.monthly[i]?.marginCents ?? 0), 0);
        const net = rev - dedByMonth[i];
        return net > 0 ? Math.round(((mar - dedByMonth[i]) / net) * 100) : null;
      };
      const last = gmAt(lastIdx); const prev = gmAt(prevIdx);
      if (last != null && prev != null) {
        const d = last - prev;
        insights.push(
          d === 0
            ? `Gross margin held at ${last}% in ${lastL}${partialNote}.`
            : `Gross margin ${d > 0 ? 'rose' : 'slipped'} ${Math.abs(d)} pts to ${last}% in ${lastL}${partialNote}.`,
        );
      }
    }

    // 3 · Fastest-growing channel (needs a real base: ≥ $100 the month before).
    let grower: { label: string; growthPct: number } | null = null;
    for (const r of rows) {
      const prev = r.monthly[prevIdx]?.revenueCents || 0;
      const last = r.monthly[lastIdx]?.revenueCents || 0;
      if (prev >= 10_000 && last > prev) {
        const g = Math.round(((last - prev) / prev) * 100);
        if (!grower || g > grower.growthPct) grower = { label: labelOf(r), growthPct: g };
      }
    }
    if (grower && !(mover && !cats.hasCosts && mover.label === grower.label)) {
      insights.push(`Fastest-growing channel: ${grower.label}, +${grower.growthPct}% vs ${prevL}${partialNote}.`);
    }

    // 4 · Deduction trend when two dated months exist; else the ledger total.
    if (dedByMonth[lastIdx] > 0 && dedByMonth[prevIdx] > 0) {
      const d = dedByMonth[lastIdx] - dedByMonth[prevIdx];
      insights.push(`Deductions ran ${fmtMoneyShort(dedByMonth[lastIdx])} in ${lastL} vs ${fmtMoneyShort(dedByMonth[prevIdx])} in ${prevL} — ${d >= 0 ? 'up' : 'down'} ${fmtMoneyShort(Math.abs(d))}.`);
    } else if (dedTotal > 0 && dedRatePct != null) {
      insights.push(`Trade deductions total ${fmtMoneyShort(dedTotal)} — ${pct1(dedRatePct)}% of revenue on the ledger.`);
    }
  }

  // 5 · Concentration — channel first when one carries most of the business, then the top account.
  const topChannel = channels[0];
  if (topChannel && topChannel.sharePct >= 50 && channels.length > 1) {
    insights.push(`${topChannel.label} carries ${topChannel.sharePct}% of revenue — more than half the business rides one channel.`);
  }
  if (topAccount && topAccountShare != null && topAccountShare >= 25 && dedCustomers.length > 1) {
    insights.push(`Top account: ${topAccount.customerName} is ${topAccountShare}% of all revenue.`);
  }

  // 6 · Cash note from A/R aging.
  if (over60Label && oldestOpenDays != null) {
    insights.push(`${over60Label} sits unpaid past 60 days (${over60Count} invoice${over60Count === 1 ? '' : 's'}, oldest ${oldestOpenDays} days) — chase it on Wholesale → Invoices.`);
  }

  return {
    grossCents,
    gmPct, dedRatePct, deliveryCents, deliveryPct, drops,
    avgDaysToPay, paidInvoiceCount, statedTermsDays,
    openArLabel, over60Label, over60Count, oldestOpenDays,
    activeAccounts, revPerAccountCents,
    channels, commentary, dedMixedCurrency,
    insights: insights.slice(0, 6),
    lastMonthLabel: lastL, lastIsPartial,
  };
}
